import { create } from "zustand";
import type {
  UserProfile,
  EventProfile,
  RaceEvent,
  WeeklyCheckIn,
  WorkoutCompletion,
} from "@/types";
import { profileService } from "@/services/profileService";
import { eventService } from "@/services/eventService";
import { checkInService } from "@/services/checkInService";
import { useAuthStore } from "@/state/authStore";
import { toast } from "@/components/Toast/useToast";
import { friendlyError } from "@/lib/errors";

type FTPEntry = { date: string; ftp: number };

/** Helper — get userId without using the React hook (safe outside components). */
function getUserId(): string | null {
  return useAuthStore.getState().account?.id ?? null;
}

// Debounce timer for updateProfile saves — merges rapid sequential updates
// (e.g. toggling chips + simultaneous FTP test) into a single DB write.
let _saveTimer: ReturnType<typeof setTimeout> | null = null;
const SAVE_DEBOUNCE_MS = 300;

/**
 * Cancel any pending debounced save and write the current profile to Supabase
 * immediately. Call this when data MUST be persisted before the user can
 * navigate away (e.g. after a Wahoo sync completes on mobile).
 */
export function flushPendingProfileSave(): void {
  if (_saveTimer !== null) {
    clearTimeout(_saveTimer);
    _saveTimer = null;
  }
  const uid = getUserId();
  // useProfileStore is defined later in this module but is always initialised
  // by the time this function is called at runtime.
  const { profile } = useProfileStore.getState();
  if (uid && profile) {
    void profileService.save(uid, profile).catch(onSaveError("flushSave"));
  }
}

/** Surface a profile save failure as a toast so the user knows data wasn't persisted. */
function onSaveError(label: string) {
  return (err: unknown) => {
    console.error(`[profileStore] ${label} save failed:`, err);
    toast.error("Your changes weren't saved", {
      message: friendlyError(err),
    });
  };
}

type ProfileStore = {
  profile: UserProfile | null;
  event: EventProfile | null;
  checkIns: WeeklyCheckIn[];
  ftpHistory: FTPEntry[];

  // Actions
  setProfile: (profile: UserProfile) => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
  setEvent: (event: EventProfile | null) => void;
  addRaceEvent: (accountId: string, race: RaceEvent) => void;
  updateRaceEvent: (raceId: string, updates: Partial<RaceEvent>) => void;
  removeRaceEvent: (raceId: string) => void;
  addCheckIn: (checkIn: WeeklyCheckIn) => void;
  getCheckIn: (weekStartDate: string) => WeeklyCheckIn | undefined;
  recordFTP: (ftp: number) => void;
  /**
   * Atomically apply arbitrary profile updates AND record an FTP history snapshot
   * in a single DB write, eliminating any race between two competing saves.
   * Use this from ProfileSettingsModal & FTPResultModal.
   */
  updateProfileWithFTP: (updates: Partial<UserProfile>, ftp: number) => void;
  /** Save a device readiness/sleep score for a specific calendar date. Pass null to clear. */
  setDailySleepScore: (
    date: string,
    score: import("@/types").ReadinessScore | null,
  ) => void;
  clearProfile: () => void;
  /** Load all profile data from Supabase into the store. */
  loadFromDB: (userId: string) => Promise<void>;
  /**
   * Update powerRecords if the incoming completion sets any personal records.
   * Only writes to Supabase when at least one key is beaten — no-ops otherwise.
   */
  updatePowerRecords: (completion: WorkoutCompletion) => void;
};

export const useProfileStore = create<ProfileStore>()((set, get) => ({
  profile: null,
  event: null,
  checkIns: [],
  ftpHistory: [],

  setProfile: (profile) => {
    // Also hydrate the in-memory ftpHistory from whatever is embedded in the profile.
    const history = profile.ftpHistory ?? [];
    set({ profile, ftpHistory: history });
    const uid = getUserId();
    if (uid)
      void profileService.save(uid, profile).catch(onSaveError("profile"));
  },

  updateProfile: (updates) => {
    // Capture the merged profile inside set() to avoid a race where a second
    // concurrent updateProfile call overwrites state before get() reads it.
    set((state) => {
      const merged = state.profile ? { ...state.profile, ...updates } : null;
      return { profile: merged };
    });
    // Debounce the DB write so rapid successive calls (e.g. toggling type chips
    // while an FTP test completes) are merged into a single round-trip.
    if (_saveTimer !== null) clearTimeout(_saveTimer);
    _saveTimer = setTimeout(() => {
      _saveTimer = null;
      const uid = getUserId();
      const { profile } = get();
      if (uid && profile)
        void profileService.save(uid, profile).catch(onSaveError("profile"));
    }, SAVE_DEBOUNCE_MS);
  },

  setEvent: (event) => {
    set({ event });
    const uid = getUserId();
    if (uid && event)
      void eventService.save(uid, event).catch(onSaveError("event"));
  },

  addRaceEvent: (accountId, race) => {
    set((state) => ({
      event: {
        accountId,
        events: [
          ...(state.event?.events ?? []).filter((e) => e.id !== race.id),
          race,
        ].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        ),
      },
    }));
    const uid = getUserId();
    if (uid) {
      const { event } = get();
      if (event) void eventService.save(uid, event).catch(onSaveError("event"));
    }
  },

  updateRaceEvent: (raceId, updates) => {
    set((state) => ({
      event: state.event
        ? {
            ...state.event,
            events: state.event.events.map((race) =>
              race.id === raceId ? { ...race, ...updates } : race,
            ),
          }
        : null,
    }));
    const uid = getUserId();
    if (uid) {
      const { event } = get();
      if (event) void eventService.save(uid, event).catch(onSaveError("event"));
    }
  },

  removeRaceEvent: (raceId) => {
    set((state) => ({
      event: state.event
        ? {
            ...state.event,
            events: state.event.events.filter((e) => e.id !== raceId),
          }
        : null,
    }));
    const uid = getUserId();
    if (uid) {
      const { event } = get();
      if (event) void eventService.save(uid, event).catch(onSaveError("event"));
    }
  },

  addCheckIn: (checkIn) => {
    set((state) => ({
      checkIns: [
        ...state.checkIns.filter(
          (c) => c.weekStartDate !== checkIn.weekStartDate,
        ),
        checkIn,
      ],
    }));
    const uid = getUserId();
    if (uid)
      void checkInService.save(uid, checkIn).catch(onSaveError("checkIn"));
  },

  getCheckIn: (weekStartDate) =>
    get().checkIns.find((c) => c.weekStartDate === weekStartDate),

  recordFTP: (ftp) => {
    const today = new Date().toISOString().slice(0, 10);
    let profileToSave: UserProfile | null = null;
    set((state) => {
      const updatedHistory = [
        ...(state.ftpHistory ?? []).filter((e) => e.date !== today),
        { date: today, ftp },
      ].slice(-52); // keep last ~1 year of weekly snapshots
      const merged: UserProfile | null = state.profile
        ? {
            ...state.profile,
            ftpWatts: ftp,
            lastFTPTestDate: today,
            ftpHistory: updatedHistory,
            // Archive the current training-block workout IDs, then reset so
            // the next 4-month cycle gets a fresh set of unique workouts.
            prevCycleWorkoutBlockIds: state.profile.seenWorkoutBlockIds ?? [],
            seenWorkoutBlockIds: [],
          }
        : null;
      profileToSave = merged;
      return { ftpHistory: updatedHistory, profile: merged };
    });
    const uid = getUserId();
    if (uid && profileToSave) {
      void profileService.save(uid, profileToSave).catch(onSaveError("recordFTP"));
      localStorage.setItem("landing_ftp", String(ftp));
    }
  },

  updateProfileWithFTP: (updates, ftp) => {
    const today = new Date().toISOString().slice(0, 10);
    let profileToSave: UserProfile | null = null;
    set((state) => {
      const updatedHistory = [
        ...(state.ftpHistory ?? []).filter((e) => e.date !== today),
        { date: today, ftp },
      ].slice(-52);
      // If there's an existing profile, merge into it.
      // If there's no profile yet (first-time save), `updates` must be a full
      // UserProfile — use it directly as the base.
      const base = state.profile ?? (updates as UserProfile);
      const merged: UserProfile = {
        ...base,
        ...updates,
        ftpWatts: ftp,
        lastFTPTestDate: today,
        ftpHistory: updatedHistory,
        // Archive the current training-block workout IDs, then reset so
        // the next 4-month cycle gets a fresh set of unique workouts.
        prevCycleWorkoutBlockIds: base.seenWorkoutBlockIds ?? [],
        seenWorkoutBlockIds: [],
      };
      profileToSave = merged;
      return { ftpHistory: updatedHistory, profile: merged };
    });
    const uid = getUserId();
    if (uid && profileToSave) {
      // Single DB write — no race between two competing saves.
      void profileService
        .save(uid, profileToSave)
        .catch(onSaveError("updateProfileWithFTP"));
      localStorage.setItem("landing_ftp", String(ftp));
    }
  },

  setDailySleepScore: (date, score) => {
    let profileToSave: UserProfile | null = null;
    set((state) => {
      if (!state.profile) return state;
      const current = { ...(state.profile.dailySleepScores ?? {}) };
      if (score === null) {
        delete current[date];
      } else {
        current[date] = score;
      }
      const merged = { ...state.profile, dailySleepScores: current };
      profileToSave = merged;
      return { profile: merged };
    });
    const uid = getUserId();
    if (uid && profileToSave)
      void profileService.save(uid, profileToSave).catch(onSaveError("sleepScore"));
  },

  clearProfile: () =>
    set({ profile: null, event: null, checkIns: [], ftpHistory: [] }),

  loadFromDB: async (userId) => {
    try {
      const [profile, event, checkIns] = await Promise.all([
        profileService.load(userId),
        eventService.load(userId),
        checkInService.loadAll(userId),
      ]);
      set({
        ...(profile !== null && { profile }),
        ...(event !== null && { event }),
        ...(checkIns.length > 0 && { checkIns }),
      });
    } catch (err) {
      console.error("[profileStore] loadFromDB failed:", err);
      toast.error("Failed to load your profile", {
        message: "Please refresh the page.",
      });
    }
  },

  updatePowerRecords: (completion) => {
    const { profile } = get();
    if (!profile) return;

    const existing = profile.powerRecords ?? {};
    const candidates: Record<string, number> = {};

    // bestEfforts from .FIT imports / Wahoo FIT auto-fetch — exact peak-window values.
    // Populates the 4 keys that deriveWeakestZone() reads to bias Feeling Lucky
    // toward the rider's weakest power system (sprint / vo2max / threshold / endurance).
    // Also feeds the all-time PR comparison line in PowerCurveChart inside RideDetailModal.
    if (completion.bestEfforts) {
      const b = completion.bestEfforts;
      if (b.sprint5s && b.sprint5s > (existing["5s"] ?? 0))
        candidates["5s"] = b.sprint5s;
      if (b.min1 && b.min1 > (existing["1min"] ?? 0))
        candidates["1min"] = b.min1;
      if (b.vo2_5min && b.vo2_5min > (existing["5min"] ?? 0))
        candidates["5min"] = b.vo2_5min;
      if (b.threshold20min && b.threshold20min > (existing["20min"] ?? 0))
        candidates["20min"] = b.threshold20min;
      if (b.endurance60min && b.endurance60min > (existing["60min"] ?? 0))
        candidates["60min"] = b.endurance60min;
    }

    // Auto-detect HR max from ride data — only update if observed value exceeds
    // current and falls within a sane physiological range (140-220 bpm).
    // Prefer true peak HR from FIT imports; fall back to heartRateAvg from
    // Wahoo rides (conservative — real max is higher, so zones stay safe).
    const observedHR = completion.bestEfforts?.maxHR ?? completion.heartRateAvg;
    const currentHR = profile.hrMax;
    const hrUpdate =
      observedHR && observedHR >= 140 && observedHR <= 220 &&
      (!currentHR || observedHR > currentHR)
        ? { hrMax: observedHR }
        : {};

    if (Object.keys(candidates).length === 0 && !hrUpdate.hrMax) return;

    get().updateProfile({
      ...(Object.keys(candidates).length > 0
        ? { powerRecords: { ...existing, ...candidates } }
        : {}),
      ...hrUpdate,
    });
  },
}));
